import React from 'react';
import { useTranslation } from 'react-i18next';

const LEVEL_OPTIONS_KEYS = [
  { value: '', labelKey: 'admin.performance.allLevels' },
  { value: 'Junior', label: 'Junior', color: '#6b7280' },
  { value: 'Rising', label: 'Rising', color: '#3B82F6' },
  { value: 'Senior', label: 'Senior', color: '#FF6B00' },
  { value: 'Elite', label: 'Elite', color: '#FFD700' },
];

const PerformancesFilters = ({
  periods,
  periodsLoading,
  selectedPeriods,
  onPeriodToggle,
  selectedServer,
  onServerChange,
  onReset,
  selectedLevel,
  onLevelChange,
  selectedManagerId,
  onManagerChange,
  managersData,
  serversData,
}) => {
  const { t } = useTranslation();

  // Build server tabs: All + dynamic servers
  const serverTabs = [
    { id: 'All', name: 'All', color: 'rgba(255,255,255,0.5)' },
    ...(serversData || [])?.map(s => ({ id: s?.id, name: s?.name || s?.id, color: s?.color || '#888888' })),
  ];

  return (
    <div
      className="flex flex-wrap items-start gap-3 px-6 py-4 border-b"
      style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}
    >
      {/* Period Multi-Select */}
      <div className="flex flex-col gap-1">
        <label className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performance.periodLabel', 'Période')}
        </label>
        <div className="flex flex-wrap gap-1.5" style={{ maxWidth: '420px' }}>
          {periodsLoading ? (
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>{t('admin.performance.loading', 'Chargement...')}</span>
          ) : (
            periods?.map((p) => {
              const isSelected = selectedPeriods?.includes(p);
              const selIdx = selectedPeriods?.indexOf(p);
              const colors = ['#E5202A', '#FF6B00', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7'];
              const activeColor = colors?.[selIdx % colors?.length] || '#E5202A';
              return (
                <button
                  key={p}
                  onClick={() => onPeriodToggle(p)}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border"
                  style={{
                    backgroundColor: isSelected ? `${activeColor}26` : '#111111',
                    color: isSelected ? activeColor : 'rgba(255,255,255,0.45)',
                    borderColor: isSelected ? `${activeColor}66` : '#1f1f1f',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {p}
                </button>
              );
            })
          )}
        </div>
      </div>
      {/* Server Tabs — dynamic */}
      <div className="flex flex-col gap-1">
        <label className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performance.serverLabel2', 'Serveur')}
        </label>
        <div className="flex items-center gap-1.5 flex-wrap">
          {serverTabs?.map(s => (
            <button
              key={s?.id}
              onClick={() => onServerChange(s?.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                backgroundColor: selectedServer === s?.id ? s?.color : '#111111',
                color: selectedServer === s?.id ? '#fff' : 'rgba(255,255,255,0.45)',
                border: `1px solid ${selectedServer === s?.id ? s?.color : '#1f1f1f'}`,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {s?.name}
            </button>
          ))}
        </div>
      </div>
      {/* Manager Dropdown — by UUID */}
      <div className="flex flex-col gap-1">
        <label className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performance.managerLabel2', 'Manager')}
        </label>
        <select
          value={selectedManagerId || ''}
          onChange={e => onManagerChange?.(e?.target?.value)}
          className="text-base rounded-lg px-3 py-1.5 outline-none border"
          style={{
            backgroundColor: '#111111',
            borderColor: selectedManagerId ? '#f97316' : '#1f1f1f',
            color: selectedManagerId ? '#f97316' : 'rgba(255,255,255,0.8)',
            fontFamily: 'Inter, sans-serif',
            minWidth: '150px',
          }}
        >
          <option value="">{t('admin.performance.allManagers', 'Tous les managers')}</option>
          {managersData?.map(m => (
            <option key={m?.id} value={m?.id}>
              {m?.name || m?.id}
            </option>
          ))}
        </select>
      </div>
      {/* Level Selector */}
      <div className="flex flex-col gap-1">
        <label className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performance.levelLabel2', 'Niveau')}
        </label>
        <select
          value={selectedLevel || ''}
          onChange={e => onLevelChange?.(e?.target?.value)}
          className="text-base rounded-lg px-3 py-1.5 outline-none border"
          style={{
            backgroundColor: '#111111',
            borderColor: selectedLevel ? (LEVEL_OPTIONS_KEYS?.find(o => o?.value === selectedLevel)?.color || '#1f1f1f') : '#1f1f1f',
            color: selectedLevel
              ? (LEVEL_OPTIONS_KEYS?.find(o => o?.value === selectedLevel)?.color || 'rgba(255,255,255,0.8)')
              : 'rgba(255,255,255,0.8)',
            fontFamily: 'Inter, sans-serif',
            minWidth: '130px',
          }}
        >
          {LEVEL_OPTIONS_KEYS?.map(opt => (
            <option key={opt?.value} value={opt?.value} style={{ color: opt?.color }}>
              {opt?.labelKey ? t(opt.labelKey) : opt?.label}
            </option>
          ))}
        </select>
      </div>
      {/* Reset */}
      <div className="flex flex-col gap-1">
        <label className="text-xs" style={{ color: 'transparent' }}>.</label>
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all border"
          style={{
            backgroundColor: 'transparent',
            borderColor: '#1f1f1f',
            color: 'rgba(255,255,255,0.4)',
            fontFamily: 'Inter, sans-serif',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--color-primary)';
            e.currentTarget.style.color = '#E5202A';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#1f1f1f';
            e.currentTarget.style.color = 'rgba(255,255,255,0.4)';
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {t('admin.performance.reset', 'Réinitialiser')}
        </button>
      </div>
    </div>
  );
};

export default PerformancesFilters;
